import type { jsPDF as JsPDF } from 'jspdf';
import { getTrip } from '@/store/tripState';
import { diffDays, sanitize, toArr } from '@/helpers';
import { toastError, toastSuccess } from '@/ui/toast';
import type {
  AgendaItem, CheckItem, CompraItem, Cotacao, Destino, Expense,
  Hotel, Transporte, Traveler, Trip, TripDoc,
} from '@/types';

export type ExportSectionId =
  | 'resumo' | 'viajantes' | 'destinos' | 'agenda' | 'transporte'
  | 'hoteis' | 'gastos' | 'checklist' | 'compras' | 'cotacoes' | 'documentos';

interface ExportSection {
  id: ExportSectionId;
  icon: string;
  label: string;
  description: string;
  singular: string;
}

interface ExportOptions {
  includeNotes: boolean;
  includeValues: boolean;
  includeCompleted: boolean;
  title: string;
}

const SECTIONS: ExportSection[] = [
  { id: 'resumo',     icon: '✦',  label: 'Visão geral',  description: 'Datas, duração, cidades e panorama financeiro', singular: 'resumo' },
  { id: 'viajantes',  icon: '👥', label: 'Viajantes',    description: 'Lista das pessoas que fazem parte da viagem', singular: 'viajante' },
  { id: 'destinos',   icon: '📍', label: 'Destinos',     description: 'Cidades, estadias, observações e bate-voltas', singular: 'destino' },
  { id: 'agenda',     icon: '📅', label: 'Agenda',       description: 'Roteiro diário com horários e locais', singular: 'atividade' },
  { id: 'transporte', icon: '✈',  label: 'Transportes',  description: 'Trechos, horários, reservas e operadoras', singular: 'trecho' },
  { id: 'hoteis',     icon: '▤',  label: 'Hospedagens',  description: 'Check-in, check-out e dados das reservas', singular: 'hospedagem' },
  { id: 'gastos',     icon: '💳', label: 'Gastos',       description: 'Lançamentos, totais por moeda e orçamento', singular: 'gasto' },
  { id: 'checklist',  icon: '✓',  label: 'Checklist',    description: 'Preparativos organizados por categoria', singular: 'item' },
  { id: 'compras',    icon: '🛍', label: 'Compras',      description: 'Lista de compras, quantidades e lojas', singular: 'item' },
  { id: 'cotacoes',   icon: '◇',  label: 'Cotações',     description: 'Alternativas pesquisadas e seus status', singular: 'cotação' },
  { id: 'documentos', icon: '📄', label: 'Documentos',   description: 'Índice seguro de documentos da viagem', singular: 'documento' },
];

const ESSENTIAL: ExportSectionId[] = [
  'resumo', 'viajantes', 'destinos', 'agenda', 'transporte', 'hoteis', 'gastos', 'checklist', 'documentos',
];

let selected = new Set<ExportSectionId>(ESSENTIAL);

function values<T>(record?: Record<string, T>): T[] {
  return Object.values(record ?? {});
}

export function getExportSectionCounts(trip: Partial<Trip>): Record<ExportSectionId, number> {
  return {
    resumo: 1,
    viajantes: values(trip.travelers).length,
    destinos: values(trip.destinos).length,
    agenda: values(trip.agenda).length,
    transporte: values(trip.transporte).length,
    hoteis: values(trip.hoteis).length,
    gastos: values(trip.expenses).length,
    checklist: values(trip.checks).length,
    compras: values(trip.compras).length,
    cotacoes: values(trip.cotacoes).length,
    documentos: values(trip.docs).length,
  };
}

function plural(count: number, singular: string): string {
  if (count === 1) return `1 ${singular}`;
  const irregular: Record<string, string> = {
    resumo: 'resumos', viajante: 'viajantes', destino: 'destinos', atividade: 'atividades',
    trecho: 'trechos', hospedagem: 'hospedagens', gasto: 'gastos', item: 'itens',
    cotação: 'cotações', documento: 'documentos',
  };
  return `${count} ${irregular[singular] ?? singular + 's'}`;
}

function selectedAvailableIds(): ExportSectionId[] {
  const counts = getExportSectionCounts(getTrip());
  return SECTIONS.filter(section => selected.has(section.id) && counts[section.id] > 0).map(section => section.id);
}

function renderSelectionSummary(): void {
  const trip = getTrip();
  const counts = getExportSectionCounts(trip);
  const ids = selectedAvailableIds();
  const itemCount = ids.reduce((sum, id) => sum + (id === 'resumo' ? 0 : counts[id]), 0);
  const estimate = Math.max(2, 1 + Math.ceil(itemCount / 7) + Math.ceil(Math.max(0, ids.length - 4) / 3));
  const countEl = document.getElementById('export-selected-count');
  const pagesEl = document.getElementById('export-page-estimate');
  const btn = document.getElementById('export-generate-btn') as HTMLButtonElement | null;
  if (countEl) countEl.textContent = `${ids.length} ${ids.length === 1 ? 'seção' : 'seções'} · ${itemCount} ${itemCount === 1 ? 'item' : 'itens'}`;
  if (pagesEl) pagesEl.textContent = `aprox. ${estimate} ${estimate === 1 ? 'página' : 'páginas'}`;
  if (btn) btn.disabled = ids.length === 0;

  document.querySelectorAll<HTMLElement>('.export-select-card').forEach(card => {
    const id = card.dataset.section as ExportSectionId;
    const on = selected.has(id) && counts[id] > 0;
    card.classList.toggle('selected', on);
    card.setAttribute('aria-pressed', String(on));
    card.querySelector('.export-check')?.classList.toggle('on', on);
  });
}

export function renderExportScreen(): void {
  const el = document.getElementById('export-screen');
  if (!el) return;
  const trip = getTrip();
  const counts = getExportSectionCounts(trip);
  const defaultTitle = `Guia da viagem · ${trip.name ?? 'Minha viagem'}`;

  el.innerHTML = `
    <div class="export-hero">
      <div class="export-hero-copy">
        <span class="export-eyebrow">GUIA PERSONALIZADO</span>
        <h2>Sua viagem, pronta para levar.</h2>
        <p>Escolha o que importa e transforme o planejamento em um PDF bonito, organizado e disponível offline.</p>
      </div>
      <div class="export-hero-art" aria-hidden="true">
        <div class="export-paper export-paper-back"></div>
        <div class="export-paper"><span>TRAVELLE</span><i></i><i></i><i></i></div>
      </div>
    </div>

    <div class="export-layout">
      <section class="export-builder">
        <div class="export-section-head">
          <div>
            <span class="export-step">01</span>
            <h3>Escolha o conteúdo</h3>
          </div>
          <div class="export-presets">
            <button class="btn-sm" onclick="window.__setExportPreset('essential')">Essencial</button>
            <button class="btn-sm" onclick="window.__setExportPreset('all')">Selecionar tudo</button>
            <button class="btn-sm" onclick="window.__setExportPreset('none')">Limpar</button>
          </div>
        </div>
        <div class="export-grid">
          ${SECTIONS.map(section => {
            const count = counts[section.id];
            const disabled = count === 0;
            const on = selected.has(section.id) && !disabled;
            return `<button class="export-select-card${on ? ' selected' : ''}${disabled ? ' disabled' : ''}"
              data-section="${section.id}" aria-pressed="${on}" ${disabled ? 'disabled' : ''}
              onclick="window.__toggleExportSection('${section.id}')">
              <span class="export-card-icon">${section.icon}</span>
              <span class="export-card-copy"><strong>${section.label}</strong><small>${section.description}</small>
                <em>${disabled ? 'Sem dados cadastrados' : plural(count, section.singular)}</em></span>
              <span class="export-check${on ? ' on' : ''}">✓</span>
            </button>`;
          }).join('')}
        </div>

        <div class="export-section-head export-options-head">
          <div><span class="export-step">02</span><h3>Ajuste os detalhes</h3></div>
        </div>
        <div class="export-options">
          <label class="export-title-field"><span>Título do documento</span>
            <input id="export-document-title" value="${sanitize(defaultTitle)}" maxlength="80">
          </label>
          <div class="export-toggle-row">
            <label class="export-toggle"><input id="export-notes" type="checkbox" checked><span></span><div><strong>Incluir observações</strong><small>Notas de atividades, destinos e itens</small></div></label>
            <label class="export-toggle"><input id="export-values" type="checkbox" checked><span></span><div><strong>Incluir valores</strong><small>Preços, gastos e orçamento</small></div></label>
            <label class="export-toggle"><input id="export-completed" type="checkbox" checked><span></span><div><strong>Incluir concluídos</strong><small>Itens já feitos em listas e agenda</small></div></label>
          </div>
        </div>
      </section>

      <aside class="export-summary-card">
        <span class="export-step">03</span>
        <h3>Seu guia está quase pronto</h3>
        <div class="export-trip-mini">
          <div class="export-trip-mark" style="background:${sanitize(trip.color ?? '#00A3E0')}">${sanitize(trip.emoji ?? '✈')}</div>
          <div><strong>${sanitize(trip.name ?? 'Minha viagem')}</strong><small>${dateRange(trip.start, trip.end)}</small></div>
        </div>
        <div class="export-summary-meta">
          <div><strong id="export-selected-count"></strong><small>conteúdo selecionado</small></div>
          <div><strong id="export-page-estimate"></strong><small>estimativa do arquivo</small></div>
        </div>
        <div class="export-privacy"><span>⌁</span><p><strong>Privado por padrão</strong><br>O arquivo é criado neste dispositivo. Seus dados não são enviados para outro serviço.</p></div>
        <button id="export-generate-btn" class="export-generate-btn" onclick="window.__generateTripPdf()">
          <span>Gerar e visualizar PDF</span><b>↗</b>
        </button>
        <small class="export-download-note">Abre em nova aba · baixe pelo visualizador</small>
      </aside>
    </div>`;

  renderSelectionSummary();
}

export function toggleExportSection(id: ExportSectionId): void {
  if (selected.has(id)) selected.delete(id); else selected.add(id);
  renderSelectionSummary();
}

export function setExportPreset(preset: 'essential' | 'all' | 'none'): void {
  const counts = getExportSectionCounts(getTrip());
  if (preset === 'none') selected.clear();
  else {
    const source = preset === 'all' ? SECTIONS.map(s => s.id) : ESSENTIAL;
    selected = new Set(source.filter(id => counts[id] > 0));
  }
  renderSelectionSummary();
}

function dateLabel(value?: string): string {
  if (!value) return 'Data a definir';
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' })
    .format(new Date(year, month - 1, day)).replace(/ de /g, ' ');
}

function dateRange(start?: string, end?: string): string {
  if (!start && !end) return 'Datas a definir';
  if (start === end || !end) return dateLabel(start);
  return `${dateLabel(start)} - ${dateLabel(end)}`;
}

function money(value: number, currency = 'BRL'): string {
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value || 0);
  } catch {
    return `${currency} ${(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  }
}

export interface TripFinancialSummary {
  primaryCurrency: string;
  transport: number;
  lodging: number;
  agenda: number;
  expensesPrimary: number;
  totalPrimary: number;
  budgetLimit: number;
  expensesByCurrency: Record<string, number>;
}

/** Matches the same cost model used by the budget screen. */
export function calculateTripFinancialSummary(trip: Partial<Trip>): TripFinancialSummary {
  const primaryCurrency = trip.currencies?.[0] ?? 'BRL';
  const transport = values(trip.transporte).reduce((sum, item) => sum + (item.preco || 0), 0);
  const lodging = values(trip.hoteis).reduce((sum, item) => sum + (item.preco || 0), 0);
  const agenda = values(trip.agenda).reduce((sum, item) => sum + (item.preco || 0), 0);
  const expensesByCurrency = values(trip.expenses).reduce<Record<string, number>>((totals, item) => {
    const currency = item.currency || primaryCurrency;
    totals[currency] = (totals[currency] ?? 0) + (item.amount || 0);
    return totals;
  }, {});
  const expensesPrimary = expensesByCurrency[primaryCurrency] ?? 0;
  return {
    primaryCurrency,
    transport,
    lodging,
    agenda,
    expensesPrimary,
    totalPrimary: transport + lodging + agenda + expensesPrimary,
    budgetLimit: trip.budgetLimit ?? 0,
    expensesByCurrency,
  };
}

function normalizePdfRichText(value: unknown): string {
  return String(value ?? '')
    .normalize('NFKC')
    // Normalize every Unicode space used by rich-text editors/importers, then
    // remove invisible formatting marks that can make PDF fonts stretch text.
    .replace(/[\u1680\u2000-\u200A\u202F\u205F\u3000]/gu, ' ')
    .replace(/[\u00AD\u034F\u061C\u180E\u200B-\u200F\u202A-\u202E\u2060-\u206F\uFEFF]/gu, '')
    // Some imported notes arrive with a space between every letter. Repair each
    // affected word before collapsing ordinary whitespace.
    .replace(/(?<!\p{L})(?:\p{L}[ \u00A0]){3,}\p{L}(?!\p{L})/gu, match => match.replace(/[ \u00A0]/g, ''))
    // Built-in PDF fonts do not encode these symbols consistently. Keep their
    // meaning using an ASCII representation instead of allowing mojibake.
    .replace(/(?:â†’|âž¡(?:ï¸)?|→|➡️?|➜|➝|⟶|⇒)/gu, ' -> ')
    .replace(/(?:â†”|↔️?|⟷|⇔)/gu, ' <-> ')
    .replace(/[–—−]/gu, '-')
    .replace(/[‘’‚‛]/gu, "'")
    .replace(/[“”„‟]/gu, '"')
    .replace(/[…]/gu, '...')
    .replace(/[·•]/gu, ' | ')
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/gu, '')
    .replace(/[^\S\r\n]+/gu, ' ')
    .replace(/ *\r?\n */gu, '\n')
    .replace(/\n{3,}/gu, '\n\n')
    .trim();
}

/** Replaces emoji semantics with readable labels before sending text to PDF fonts. */
export function normalizePdfText(value: unknown): string {
  return normalizePdfRichText(value)
    .replace(/[🌟⭐💡]\uFE0F?\s*(?=Dica\b)/giu, '')
    .replace(/[⏱⏰⌛]\uFE0F?\s*(?=Tempo\b)/giu, '')
    .replace(/📍\uFE0F?\s*(?=Local\b)/giu, '')
    .replace(/[🎟🎫]\uFE0F?\s*(?=Ingresso\b)/giu, '')
    .replace(/[🍽🍴]\uFE0F?\s*(?=Alimenta(?:ção|cao)\b)/giu, '')
    .replace(/[📝📌]\uFE0F?\s*(?=Nota\b)/giu, '')
    .replace(/[🌟⭐💡]/gu, 'Dica - ')
    .replace(/[⏱⏰⌛]/gu, 'Tempo - ')
    .replace(/📍/gu, 'Local - ')
    .replace(/[🎟🎫]/gu, 'Ingresso - ')
    .replace(/[🍽🍴]/gu, 'Alimentação - ')
    .replace(/[📝📌]/gu, 'Nota - ')
    .replace(/[\uFE0F\u200D]/gu, '')
    .replace(/\p{Extended_Pictographic}/gu, '')
    .replace(/[^\S\r\n]{2,}/g, ' ')
    .trim();
}

function payerName(trip: Partial<Trip>, id?: string): string {
  return id ? trip.travelers?.[id]?.name ?? '' : '';
}

function safeFilePart(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase();
}

export function exportFileName(tripName?: string): string {
  return `travelle-${safeFilePart(tripName || 'minha-viagem') || 'minha-viagem'}.pdf`;
}

function hexToRgb(hex?: string): [number, number, number] {
  const clean = (hex ?? '#00A3E0').replace('#', '');
  if (!/^[0-9a-f]{6}$/i.test(clean)) return [0, 163, 224];
  return [parseInt(clean.slice(0, 2), 16), parseInt(clean.slice(2, 4), 16), parseInt(clean.slice(4, 6), 16)];
}

type PdfColor = [number, number, number];

interface PdfTheme {
  accent: PdfColor;
  tint: PdfColor;
  emoji: string;
}

const PDF_THEMES: Record<string, PdfTheme> = {
  food:      { accent: [230, 116, 55],  tint: [255, 244, 235], emoji: '🍴' },
  culture:   { accent: [119, 79, 191],  tint: [246, 240, 255], emoji: '🏛️' },
  transport: { accent: [37, 126, 184],  tint: [236, 247, 255], emoji: '🚆' },
  nature:    { accent: [45, 145, 94],   tint: [237, 250, 243], emoji: '🌿' },
  lodging:   { accent: [32, 151, 145],  tint: [235, 250, 249], emoji: '🛏️' },
  shopping:  { accent: [199, 76, 133],  tint: [255, 239, 247], emoji: '🛍️' },
  money:     { accent: [33, 139, 112],  tint: [235, 249, 244], emoji: '💳' },
  checklist: { accent: [76, 120, 196],  tint: [239, 244, 255], emoji: '✅' },
  docs:      { accent: [91, 103, 122],  tint: [242, 245, 248], emoji: '📄' },
  general:   { accent: [64, 109, 190],  tint: [239, 245, 255], emoji: '✨' },
};

function categoryTheme(value?: string): PdfTheme {
  const key = String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  if (/gastr|aliment|restaur|comida|cafe|bar|drink/.test(key)) return PDF_THEMES.food;
  if (/cultur|museu|hist|arte|palacio|igreja|teatro|show/.test(key)) return PDF_THEMES.culture;
  if (/transport|voo|trem|metro|onibus|carro|desloc/.test(key)) return PDF_THEMES.transport;
  if (/nature|parque|praia|trilha|jardim/.test(key)) return PDF_THEMES.nature;
  if (/hotel|hosped|pousada/.test(key)) return PDF_THEMES.lodging;
  if (/compra|shopping|loja/.test(key)) return PDF_THEMES.shopping;
  return PDF_THEMES.general;
}

function sectionTheme(title: string): PdfTheme {
  const key = title.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  if (/agenda|destino/.test(key)) return PDF_THEMES.culture;
  if (/transport/.test(key)) return PDF_THEMES.transport;
  if (/hosped/.test(key)) return PDF_THEMES.lodging;
  if (/gasto|visao geral/.test(key)) return PDF_THEMES.money;
  if (/checklist/.test(key)) return PDF_THEMES.checklist;
  if (/compra|cotac/.test(key)) return PDF_THEMES.shopping;
  if (/document/.test(key)) return PDF_THEMES.docs;
  return PDF_THEMES.general;
}

const emojiCache = new Map<string, string | null>();

function emojiImageData(emoji: string): string | null {
  if (emojiCache.has(emoji)) return emojiCache.get(emoji) ?? null;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 96; canvas.height = 96;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.clearRect(0, 0, 96, 96);
    ctx.font = '64px "Segoe UI Emoji", "Apple Color Emoji", sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(emoji, 48, 52);
    const data = canvas.toDataURL('image/png');
    emojiCache.set(emoji, data);
    return data;
  } catch {
    emojiCache.set(emoji, null);
    return null;
  }
}

interface RasterNote {
  data: string;
  heightMm: number;
  lineCount: number;
}

function rasterNoteData(value: unknown, widthMm: number, maxLines: number): RasterNote | null {
  const text = normalizePdfRichText(value);
  if (!text) return null;
  try {
    const scale = 2;
    const pxPerMm = 3.78 * scale;
    const widthPx = Math.max(1, Math.round(widthMm * pxPerMm));
    const fontPx = 10.25 * scale;
    const lineHeightPx = 4.2 * pxPerMm;
    const measureCanvas = document.createElement('canvas');
    const measure = measureCanvas.getContext('2d');
    if (!measure) return null;
    measure.font = `${fontPx}px Inter, Arial, "Segoe UI Emoji", "Apple Color Emoji", sans-serif`;

    const lines: string[] = [];
    const pushParagraph = (paragraph: string): void => {
      if (!paragraph) { lines.push(''); return; }
      let line = '';
      paragraph.split(/\s+/u).forEach(word => {
        const candidate = line ? `${line} ${word}` : word;
        if (!line || measure.measureText(candidate).width <= widthPx) line = candidate;
        else { lines.push(line); line = word; }
      });
      if (line) lines.push(line);
    };
    text.split('\n').forEach(pushParagraph);
    const visibleLines = lines.slice(0, maxLines);
    if (lines.length > maxLines && visibleLines.length) visibleLines[visibleLines.length - 1] += ' [...]';

    const heightPx = Math.max(1, Math.ceil(visibleLines.length * lineHeightPx));
    const canvas = document.createElement('canvas');
    canvas.width = widthPx; canvas.height = heightPx;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.font = `${fontPx}px Inter, Arial, "Segoe UI Emoji", "Apple Color Emoji", sans-serif`;
    ctx.fillStyle = '#536172'; ctx.textBaseline = 'top';
    visibleLines.forEach((line, index) => ctx.fillText(line, 0, index * lineHeightPx));
    return { data: canvas.toDataURL('image/png'), heightMm: visibleLines.length * 4.2, lineCount: visibleLines.length };
  } catch { return null; }
}

async function rasterImageData(url: string, width: number, height: number, format: 'image/png' | 'image/jpeg'): Promise<string | null> {
  try {
    const image = new Image();
    image.crossOrigin = 'anonymous'; image.src = url;
    await new Promise<void>((resolve, reject) => { image.onload = () => resolve(); image.onerror = () => reject(); });
    const canvas = document.createElement('canvas');
    canvas.width = width; canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
    const w = image.naturalWidth * scale; const h = image.naturalHeight * scale;
    ctx.drawImage(image, (width - w) / 2, (height - h) / 2, w, h);
    return canvas.toDataURL(format, .88);
  } catch { return null; }
}

async function coverImageData(url?: string): Promise<string | null> {
  return url ? rasterImageData(url, 1400, 600, 'image/jpeg') : null;
}

class PdfComposer {
  private y = 20;
  private readonly margin = 18;
  private readonly width = 210;
  private readonly height = 297;
  private sectionTitle = '';
  private contentStarted = false;
  private readonly accent: [number, number, number];
  private currentTheme: PdfTheme = PDF_THEMES.general;

  constructor(private doc: JsPDF, private trip: Partial<Trip>, private options: ExportOptions, private brandIcon: string | null) {
    this.accent = hexToRgb(trip.color);
  }

  private drawBrand(x: number, y: number, size: number, withName = false): void {
    if (this.brandIcon) this.doc.addImage(this.brandIcon, 'PNG', x, y, size, size, 'travelle-brand', 'FAST');
    else {
      this.doc.setFillColor(232, 247, 253); this.doc.circle(x + size / 2, y + size / 2, size / 2, 'F');
      this.doc.setDrawColor(0, 163, 224); this.doc.setLineWidth(.5); this.doc.circle(x + size / 2, y + size / 2, size * .36, 'S');
      this.doc.line(x + size * .2, y + size / 2, x + size * .8, y + size / 2);
    }
    if (withName) {
      this.doc.setFont('helvetica', 'bold'); this.doc.setFontSize(11); this.doc.setTextColor(15, 25, 35);
      this.doc.text('Travelle', x + size + 3, y + size * .68);
    }
  }

  cover(imageData: string | null): void {
    const [r, g, b] = this.accent;
    this.doc.setFillColor(247, 249, 252); this.doc.rect(0, 0, this.width, this.height, 'F');
    if (imageData) this.doc.addImage(imageData, 'JPEG', 0, 0, this.width, 88, undefined, 'FAST');
    else {
      this.doc.setFillColor(r, g, b); this.doc.rect(0, 0, this.width, 88, 'F');
      this.doc.setFillColor(250, 181, 64); this.doc.circle(176, 5, 36, 'F');
      this.doc.setFillColor(239, 94, 103); this.doc.circle(202, 61, 34, 'F');
      this.doc.setFillColor(92, 200, 171); this.doc.circle(143, 84, 24, 'F');
    }
    this.doc.setFillColor(255, 255, 255); this.doc.roundedRect(16, 13, 42, 15, 4, 4, 'F');
    this.drawBrand(20, 16, 9, true);
    this.doc.setFillColor(r, g, b); this.doc.roundedRect(18, 75, 48, 8, 4, 4, 'F');
    this.doc.setTextColor(255, 255, 255); this.doc.setFont('helvetica', 'bold'); this.doc.setFontSize(8); this.doc.text('TRAVELLE  /  GUIA DE VIAGEM', 22, 80.4);

    this.doc.setTextColor(15, 25, 35); this.doc.setFontSize(27); this.doc.setFont('helvetica', 'bold');
    const titleLines = this.doc.splitTextToSize(normalizePdfText(this.options.title), 165) as string[];
    this.doc.text(titleLines, 18, 111, { lineHeightFactor: 1.08 });
    let y = 111 + titleLines.length * 10.5 + 7;
    this.doc.setFont('helvetica', 'normal'); this.doc.setFontSize(12); this.doc.setTextColor(82, 96, 112);
    this.doc.text(dateRange(this.trip.start, this.trip.end), 18, y);
    y += 19;
    this.doc.setDrawColor(221, 227, 234); this.doc.line(18, y, 192, y); y += 16;
    const days = this.trip.start && this.trip.end ? Math.max(1, diffDays(this.trip.start, this.trip.end) + 1) : 0;
    const stats = [
      [days ? String(days) : '-', 'DIAS'],
      [String(values(this.trip.destinos).length), 'DESTINOS'],
      [String(values(this.trip.travelers).length), 'VIAJANTES'],
    ];
    stats.forEach(([value, label], index) => {
      const x = 18 + index * 56;
      this.doc.setTextColor(r, g, b); this.doc.setFont('helvetica', 'bold'); this.doc.setFontSize(22); this.doc.text(value, x, y + 8);
      this.doc.setTextColor(108, 122, 139); this.doc.setFontSize(7.5); this.doc.text(label, x, y + 15);
    });
    this.doc.setFont('helvetica', 'normal'); this.doc.setFontSize(8); this.doc.setTextColor(130, 142, 155);
    this.doc.text(`Gerado em ${dateLabel(new Date().toISOString().slice(0, 10))}`, 18, 278);
  }

  section(title: string, subtitle: string): void {
    if (!this.contentStarted) {
      this.doc.addPage();
      this.contentStarted = true;
      this.y = 20;
    } else {
      // Keep the heading with enough room for the first content block.
      this.ensure(70);
      this.y += 7;
    }
    this.sectionTitle = normalizePdfText(title);
    this.currentTheme = sectionTheme(this.sectionTitle);
    const [r, g, b] = this.currentTheme.accent;
    this.doc.setFillColor(r, g, b); this.doc.roundedRect(this.margin, this.y, 4, 22, 2, 2, 'F');
    this.doc.setTextColor(15, 25, 35); this.doc.setFont('helvetica', 'bold'); this.doc.setFontSize(18); this.doc.text(this.sectionTitle, this.margin + 9, this.y + 8);
    const sectionEmoji = emojiImageData(this.currentTheme.emoji);
    if (sectionEmoji) this.doc.addImage(sectionEmoji, 'PNG', this.width - this.margin - 9, this.y + 1, 8, 8, `section-${this.currentTheme.accent.join('-')}`, 'FAST');
    this.doc.setFont('helvetica', 'normal'); this.doc.setFontSize(8.5); this.doc.setTextColor(104, 118, 135);
    this.doc.text(normalizePdfText(subtitle), this.margin + 9, this.y + 15);
    this.y += 28;
  }

  private nextPage(): void {
    this.doc.addPage(); this.y = 17;
    this.doc.setFont('helvetica', 'bold'); this.doc.setFontSize(8); this.doc.setTextColor(112, 126, 142);
    this.doc.text(normalizePdfText(this.sectionTitle).toUpperCase(), this.margin, this.y); this.y += 7;
    this.doc.setDrawColor(230, 234, 239); this.doc.line(this.margin, this.y, this.width - this.margin, this.y); this.y += 8;
  }

  private ensure(space: number): void { if (this.y + space > 275) this.nextPage(); }

  metricGrid(metrics: [string, string][]): void {
    const columns = 3; const gap = 4; const w = (174 - gap * (columns - 1)) / columns;
    metrics.forEach(([value, label], index) => {
      if (index > 0 && index % columns === 0) this.y += 26;
      this.ensure(24);
      const x = this.margin + (index % columns) * (w + gap);
      this.doc.setFillColor(247, 249, 252); this.doc.roundedRect(x, this.y, w, 22, 2, 2, 'F');
      this.doc.setFont('helvetica', 'bold'); this.doc.setFontSize(12); this.doc.setTextColor(15, 25, 35); this.doc.text(normalizePdfText(value), x + 4, this.y + 9);
      this.doc.setFont('helvetica', 'normal'); this.doc.setFontSize(7); this.doc.setTextColor(105, 119, 136); this.doc.text(normalizePdfText(label).toUpperCase(), x + 4, this.y + 16);
    });
    this.y += 29;
  }

  group(label: string): void {
    // Avoid leaving a group label orphaned at the bottom of a page.
    this.ensure(30); this.doc.setFont('helvetica', 'bold'); this.doc.setFontSize(8); this.doc.setTextColor(...this.currentTheme.accent);
    this.doc.text(normalizePdfText(label).toUpperCase(), this.margin, this.y + 4); this.y += 10;
  }

  card(title: string, meta: string[], note = '', badge = ''): void {
    this.doc.setCharSpace(0);
    const safeTitle = normalizePdfText(title || 'Sem título');
    const safeBadge = normalizePdfText(badge);
    const titleLines = this.doc.splitTextToSize(safeTitle, safeBadge ? 132 : 158) as string[];
    const metaLines = meta.filter(Boolean).flatMap(line => this.doc.splitTextToSize(normalizePdfText(line), 158) as string[]);
    const noteRaster = note && this.options.includeNotes ? rasterNoteData(note, 150, 14) : null;
    const allNoteLines = !noteRaster && note && this.options.includeNotes ? this.doc.splitTextToSize(normalizePdfText(note), 150) as string[] : [];
    const noteLines = allNoteLines.slice(0, 14);
    if (allNoteLines.length > noteLines.length && noteLines.length) noteLines[noteLines.length - 1] += ' [...]';
    const noteHeight = noteRaster ? noteRaster.heightMm + 7 : noteLines.length ? noteLines.length * 4.2 + 7 : 0;
    const h = 11 + titleLines.length * 5 + metaLines.length * 4.2 + noteHeight;
    this.ensure(h + 4);
    const y = this.y;
    this.doc.setFillColor(255, 255, 255); this.doc.setDrawColor(224, 230, 237); this.doc.roundedRect(this.margin, y, 174, h, 3, 3, 'FD');
    this.doc.setFillColor(...this.currentTheme.accent); this.doc.roundedRect(this.margin, y, 2.2, h, 1, 1, 'F');
    this.doc.setFont('helvetica', 'bold'); this.doc.setFontSize(10.5); this.doc.setTextColor(22, 32, 44); this.doc.text(titleLines, this.margin + 7, y + 7.5);
    if (safeBadge) {
      const badgeWidth = Math.min(38, this.doc.getTextWidth(safeBadge.toUpperCase()) + 7);
      this.doc.setFillColor(...this.currentTheme.tint); this.doc.roundedRect(188 - badgeWidth, y + 3.5, badgeWidth, 6, 3, 3, 'F');
      this.doc.setFontSize(6.3); this.doc.setTextColor(...this.currentTheme.accent); this.doc.text(safeBadge.toUpperCase(), 188 - badgeWidth / 2, y + 7.5, { align: 'center' });
    }
    let textY = y + 8 + titleLines.length * 5;
    this.doc.setFont('helvetica', 'normal'); this.doc.setFontSize(8.2); this.doc.setTextColor(91, 105, 122);
    metaLines.forEach(line => { this.doc.text(line, this.margin + 7, textY); textY += 4.2; });
    if (noteRaster || noteLines.length) {
      textY += 2; this.doc.setFillColor(...this.currentTheme.tint); this.doc.roundedRect(this.margin + 6, textY - 3, 160, noteHeight - 1, 2, 2, 'F');
      if (noteRaster) this.doc.addImage(noteRaster.data, 'PNG', this.margin + 10, textY - 1, 150, noteRaster.heightMm, undefined, 'FAST');
      else {
        this.doc.setFont('helvetica', 'normal'); this.doc.setFontSize(7.8); this.doc.setTextColor(91, 105, 122);
        noteLines.forEach(line => {
          this.doc.setCharSpace(0);
          this.doc.text(String(line), this.margin + 10, textY, { charSpace: 0 });
          textY += 4.2;
        });
      }
    }
    this.y += h + 4;
  }

  financial(summary: TripFinancialSummary): void {
    this.ensure(72);
    const y = this.y;
    const [r, g, b] = this.accent;
    this.doc.setFillColor(15, 25, 35); this.doc.roundedRect(this.margin, y, 174, 34, 4, 4, 'F');
    this.doc.setFont('helvetica', 'normal'); this.doc.setFontSize(7.5); this.doc.setTextColor(191, 202, 214); this.doc.text('CUSTO TOTAL NA MOEDA PRINCIPAL', this.margin + 8, y + 9);
    this.doc.setFont('helvetica', 'bold'); this.doc.setFontSize(20); this.doc.setTextColor(255, 255, 255); this.doc.text(money(summary.totalPrimary, summary.primaryCurrency), this.margin + 8, y + 21);
    if (summary.budgetLimit > 0) {
      const pct = Math.min(100, Math.round(summary.totalPrimary / summary.budgetLimit * 100));
      this.doc.setFont('helvetica', 'normal'); this.doc.setFontSize(7.5); this.doc.setTextColor(191, 202, 214); this.doc.text(`${pct}% de ${money(summary.budgetLimit, summary.primaryCurrency)}`, this.margin + 8, y + 29);
      this.doc.setFillColor(55, 67, 80); this.doc.roundedRect(this.margin + 104, y + 24, 60, 3, 1.5, 1.5, 'F');
      this.doc.setFillColor(r, g, b); this.doc.roundedRect(this.margin + 104, y + 24, 60 * pct / 100, 3, 1.5, 1.5, 'F');
    }
    this.y += 40;
    const rows: [string, number][] = [
      ['Transportes', summary.transport], ['Hospedagens', summary.lodging],
      ['Agenda e atrações', summary.agenda], ['Gastos registrados', summary.expensesPrimary],
    ];
    rows.forEach(([label, value], index) => {
      const rowY = this.y + index * 7;
      if (index % 2 === 0) { this.doc.setFillColor(247, 249, 252); this.doc.rect(this.margin, rowY - 4.5, 174, 7, 'F'); }
      this.doc.setFont('helvetica', 'normal'); this.doc.setFontSize(8); this.doc.setTextColor(91, 105, 122); this.doc.text(label, this.margin + 3, rowY);
      this.doc.setFont('helvetica', 'bold'); this.doc.setTextColor(26, 37, 49); this.doc.text(money(value, summary.primaryCurrency), this.margin + 171, rowY, { align: 'right' });
    });
    this.y += rows.length * 7 + 3;
    const foreign = Object.entries(summary.expensesByCurrency).filter(([currency]) => currency !== summary.primaryCurrency);
    if (foreign.length) {
      this.doc.setFont('helvetica', 'normal'); this.doc.setFontSize(7.5); this.doc.setTextColor(104, 118, 135);
      this.doc.text(`Outras moedas: ${foreign.map(([cur, amount]) => money(amount, cur)).join(' | ')} (sem conversão)`, this.margin + 3, this.y);
      this.y += 7;
    }
  }

  agendaDay(date: string, count: number): void {
    // Reserve room for the day bar and the beginning of its first activity.
    this.ensure(48);
    const [r, g, b] = PDF_THEMES.culture.accent;
    this.doc.setFillColor(r, g, b); this.doc.roundedRect(this.margin, this.y, 174, 11, 3, 3, 'F');
    this.doc.setFont('helvetica', 'bold'); this.doc.setFontSize(8.5); this.doc.setTextColor(255, 255, 255); this.doc.text(normalizePdfText(date).toUpperCase(), this.margin + 6, this.y + 7);
    this.doc.setFont('helvetica', 'normal'); this.doc.setFontSize(7); this.doc.text(`${count} ${count === 1 ? 'atividade' : 'atividades'}`, this.margin + 168, this.y + 7, { align: 'right' });
    this.y += 15;
  }

  agendaItem(item: AgendaItem, trip: Partial<Trip>): void {
    this.doc.setCharSpace(0);
    const theme = categoryTheme(item.cat);
    const titleLines = this.doc.splitTextToSize(normalizePdfText(item.title), 116) as string[];
    const noteRaster = this.options.includeNotes ? rasterNoteData(item.notes, 124, 16) : null;
    const noteSource = !noteRaster && this.options.includeNotes ? normalizePdfText(item.notes) : '';
    const allNoteLines = noteSource ? this.doc.splitTextToSize(noteSource, 124) as string[] : [];
    const noteLines = allNoteLines.slice(0, 16);
    if (allNoteLines.length > noteLines.length && noteLines.length) noteLines[noteLines.length - 1] += ' [...]';
    const meta = [item.local, item.cat].filter(Boolean).map(normalizePdfText).join(' | ');
    const titleExtra = Math.max(0, titleLines.length - 1) * 5;
    const noteLineCount = noteRaster?.lineCount ?? noteLines.length;
    const h = 20 + titleExtra + (meta ? 5 : 0) + (noteLineCount ? noteLineCount * 4.2 + 8 : 0) + (this.options.includeValues && item.preco ? 5 : 0);
    this.ensure(h + 4);
    const y = this.y;
    this.doc.setDrawColor(222, 229, 236); this.doc.setFillColor(255, 255, 255); this.doc.roundedRect(this.margin, y, 174, h, 3, 3, 'FD');
    this.doc.setFillColor(...theme.tint); this.doc.roundedRect(this.margin, y, 28, h, 3, 3, 'F');
    this.doc.setFont('helvetica', 'bold'); this.doc.setFontSize(11); this.doc.setTextColor(...theme.accent); this.doc.text(item.time || '--:--', this.margin + 14, y + 11, { align: 'center' });
    this.doc.setFont('helvetica', 'normal'); this.doc.setFontSize(6.5); this.doc.setTextColor(115, 128, 143); this.doc.text(item.status === 'feito' || item.done ? 'CONCLUÍDO' : normalizePdfText(item.status || 'PLANEJADO').toUpperCase(), this.margin + 14, y + 17, { align: 'center' });
    const x = this.margin + 35;
    const emoji = emojiImageData(theme.emoji);
    this.doc.setFillColor(...theme.tint); this.doc.circle(x + 3, y + 5.5, 3.5, 'F');
    if (emoji) this.doc.addImage(emoji, 'PNG', x, y + 2.5, 6, 6, `emoji-${theme.accent.join('-')}`, 'FAST');
    this.doc.setFont('helvetica', 'bold'); this.doc.setFontSize(10.5); this.doc.setTextColor(20, 31, 43); this.doc.text(titleLines, x + 8, y + 8);
    let textY = y + 15 + titleExtra;
    if (meta) { this.doc.setFont('helvetica', 'normal'); this.doc.setFontSize(7.8); this.doc.setTextColor(91, 105, 122); this.doc.text(meta, x, textY); textY += 5; }
    if (this.options.includeValues && item.preco) {
      const payer = payerName(trip, item.pagador);
      this.doc.setFont('helvetica', 'bold'); this.doc.setFontSize(7.8); this.doc.setTextColor(...theme.accent); this.doc.text(`${money(item.preco)}${payer ? ` | ${normalizePdfText(payer)}` : ''}`, x, textY); textY += 5;
    }
    if (noteRaster || noteLines.length) {
      this.doc.setFillColor(...theme.tint); this.doc.roundedRect(x - 2, textY - 2.5, 132, noteLineCount * 4.2 + 5, 2, 2, 'F');
      if (noteRaster) this.doc.addImage(noteRaster.data, 'PNG', x + 2, textY - .5, 124, noteRaster.heightMm, undefined, 'FAST');
      else {
        this.doc.setFont('helvetica', 'normal'); this.doc.setFontSize(7.7); this.doc.setTextColor(83, 97, 114);
        noteLines.forEach(line => {
          this.doc.setCharSpace(0);
          this.doc.text(String(line), x + 2, textY + 1, { charSpace: 0 });
          textY += 4.2;
        });
      }
    }
    this.y += h + 4;
  }

  empty(message: string): void {
    this.ensure(22); this.doc.setFillColor(248, 250, 252); this.doc.roundedRect(this.margin, this.y, 174, 18, 2, 2, 'F');
    this.doc.setFont('helvetica', 'italic'); this.doc.setFontSize(9); this.doc.setTextColor(120, 133, 148); this.doc.text(message, 105, this.y + 11, { align: 'center' }); this.y += 24;
  }

  finish(): void {
    const total = this.doc.getNumberOfPages();
    for (let page = 1; page <= total; page++) {
      this.doc.setPage(page);
      if (page > 1) {
        this.doc.setDrawColor(230, 234, 239); this.doc.line(this.margin, 283, this.width - this.margin, 283);
        this.doc.setFont('helvetica', 'normal'); this.doc.setFontSize(7.5); this.doc.setTextColor(126, 138, 151);
        this.drawBrand(this.margin, 285, 5);
        this.doc.text(`Travelle  |  ${normalizePdfText(this.trip.name ?? 'Minha viagem')}`, this.margin + 8, 289);
        this.doc.text(`${page} / ${total}`, this.width - this.margin, 289, { align: 'right' });
      }
    }
    this.doc.setProperties({
      title: this.options.title,
      subject: `Guia de viagem - ${this.trip.name ?? 'Minha viagem'}`,
      author: 'Travelle', creator: 'Travelle',
    });
  }
}

function sorted<T>(items: T[], field: keyof T): T[] {
  return [...items].sort((a, b) => String(a[field] ?? '').localeCompare(String(b[field] ?? ''), 'pt-BR'));
}

function renderPdfSection(composer: PdfComposer, id: ExportSectionId, trip: Partial<Trip>, options: ExportOptions): void {
  if (id === 'resumo') {
    composer.section('Visão geral', 'Os números e informações essenciais da viagem.');
    const days = trip.start && trip.end ? Math.max(1, diffDays(trip.start, trip.end) + 1) : 0;
    composer.metricGrid([
      [days ? String(days) : '-', 'dias de viagem'],
      [String(values(trip.destinos).length), 'destinos'],
      [String(values(trip.agenda).length), 'atividades'],
      [String(values(trip.travelers).length), 'viajantes'],
      [String(values(trip.transporte).length), 'trechos'],
      [String(values(trip.hoteis).length), 'hospedagens'],
    ]);
    composer.card(trip.name ?? 'Minha viagem', [dateRange(trip.start, trip.end), `Moedas: ${(trip.currencies ?? ['BRL']).join(', ')}`], '', 'planejamento');
    if (options.includeValues) {
      composer.group('Panorama financeiro');
      composer.financial(calculateTripFinancialSummary(trip));
    }
    return;
  }

  if (id === 'viajantes') {
    const items = sorted(toArr(trip.travelers ?? {}) as Traveler[], 'name');
    composer.section('Viajantes', `${items.length} ${items.length === 1 ? 'pessoa' : 'pessoas'} nesta viagem.`);
    items.forEach((item, index) => composer.card(`${String(index + 1).padStart(2, '0')}  ${item.name}`, [item.email || 'Sem e-mail informado']));
    return;
  }

  if (id === 'destinos') {
    const items = sorted(toArr(trip.destinos ?? {}) as Destino[], 'chegada');
    composer.section('Destinos', 'Cidades na ordem da viagem, incluindo passeios de um dia.');
    items.forEach(item => {
      const dayTrips = sorted(toArr(item.dayTrips ?? {}), 'data');
      composer.card(`${item.cidade}${item.pais ? `, ${item.pais}` : ''}`, [dateRange(item.chegada, item.saida), dayTrips.length ? `${dayTrips.length} bate-volta(s): ${dayTrips.map(d => d.cidade).join(', ')}` : ''], item.notas);
      if (dayTrips.length) dayTrips.forEach(day => composer.card(`Bate-volta · ${day.cidade}`, [dateLabel(day.data)], day.notas, 'passeio'));
    });
    return;
  }

  if (id === 'agenda') {
    let items = sorted(toArr(trip.agenda ?? {}) as AgendaItem[], 'date');
    if (!options.includeCompleted) items = items.filter(item => !item.done && item.status !== 'feito');
    composer.section('Agenda', 'Seu roteiro por dia, com horários, locais e observações úteis.');
    const ordered = items.sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`));
    const byDate = ordered.reduce<Record<string, AgendaItem[]>>((groups, item) => {
      (groups[item.date || 'Sem data'] ??= []).push(item);
      return groups;
    }, {});
    Object.entries(byDate).forEach(([date, dayItems]) => {
      composer.agendaDay(date === 'Sem data' ? date : dateLabel(date), dayItems.length);
      dayItems.forEach(item => composer.agendaItem(item, trip));
    });
    if (!items.length) composer.empty('Nenhuma atividade corresponde às opções escolhidas.');
    return;
  }

  if (id === 'transporte') {
    const items = sorted(toArr(trip.transporte ?? {}) as Transporte[], 'data');
    composer.section('Transportes', 'Todos os deslocamentos e referências de reserva.');
    items.forEach(item => {
      const horaPartida = item.horaPartida || item.hora || '';
      const partida = [item.data ? dateLabel(item.data) : '', horaPartida].filter(Boolean).join(' às ');
      const chegada = [item.dataChegada ? dateLabel(item.dataChegada) : '', item.horaChegada].filter(Boolean).join(' às ');
      const trajeto = [
        partida ? `partida ${partida}` : '',
        chegada ? `chegada ${chegada}` : '',
      ].filter(Boolean).join(' - ');
      const meta = [
        trajeto,
        [item.operadora, item.num ? `nº ${item.num}` : ''].filter(Boolean).join(' · '),
      ].filter(Boolean);
      if (options.includeValues && item.preco) meta.push(`${money(item.preco)}${payerName(trip, item.pagador) ? ` · pago por ${payerName(trip, item.pagador)}` : ''}`);
      composer.card(`${item.tipo || 'Transporte'} · ${item.origem} - ${item.destino}`, meta, '', item.status);
    });
    return;
  }

  if (id === 'hoteis') {
    const items = sorted(toArr(trip.hoteis ?? {}) as Hotel[], 'checkin');
    composer.section('Hospedagens', 'Reservas e horários de entrada e saída.');
    items.forEach(item => {
      const meta = [
        `${dateLabel(item.checkin)}${item.checkinTime ? ` às ${item.checkinTime}` : ''} - ${dateLabel(item.checkout)}${item.checkoutTime ? ` às ${item.checkoutTime}` : ''}`,
        `${item.cidade || 'Cidade não informada'} · ${item.noites || 0} noite(s)${item.reserva ? ` · reserva ${item.reserva}` : ''}`,
      ];
      if (options.includeValues && item.preco) meta.push(`${money(item.preco)}${payerName(trip, item.pagador) ? ` · pago por ${payerName(trip, item.pagador)}` : ''}`);
      composer.card(item.nome, meta, '', item.status);
    });
    return;
  }

  if (id === 'gastos') {
    const items = sorted(toArr(trip.expenses ?? {}) as Expense[], 'date');
    composer.section('Gastos', 'Lançamentos e totais financeiros da viagem.');
    const totals = items.reduce<Record<string, number>>((acc, item) => { acc[item.currency || 'BRL'] = (acc[item.currency || 'BRL'] ?? 0) + (item.amount || 0); return acc; }, {});
    if (options.includeValues) composer.metricGrid(Object.entries(totals).map(([currency, amount]) => [money(amount, currency), `total em ${currency}`]));
    let currentDate = '';
    items.forEach(item => {
      if (item.date !== currentDate) { currentDate = item.date; composer.group(dateLabel(item.date)); }
      composer.card(item.desc, [item.cat, payerName(trip, item.pagador) ? `Pago por ${payerName(trip, item.pagador)}` : '', options.includeValues ? money(item.amount, item.currency) : ''].filter(Boolean), item.notes);
    });
    return;
  }

  if (id === 'checklist') {
    let items = sorted(toArr(trip.checks ?? {}) as CheckItem[], 'cat');
    if (!options.includeCompleted) items = items.filter(item => !item.done);
    composer.section('Checklist', 'Preparativos agrupados para acompanhar antes da partida.');
    let group = '';
    items.forEach(item => { if (item.cat !== group) { group = item.cat; composer.group(group); } composer.card(`${item.done ? '[x]' : '[ ]'}  ${item.texto}`, [], '', item.done ? 'concluído' : 'pendente'); });
    if (!items.length) composer.empty('Nenhum item corresponde às opções escolhidas.');
    return;
  }

  if (id === 'compras') {
    let items = sorted(toArr(trip.compras ?? {}) as CompraItem[], 'cat');
    if (!options.includeCompleted) items = items.filter(item => !item.done);
    composer.section('Lista de compras', 'Itens organizados por categoria para facilitar as compras.');
    let group = '';
    items.forEach(item => {
      if (item.cat !== group) { group = item.cat; composer.group(group); }
      composer.card(`${item.done ? '[x]' : '[ ]'}  ${item.descricao}`, [[item.marca, item.qty ? `Qtd. ${item.qty}` : '', item.loja].filter(Boolean).join(' · ')], item.notas, item.done ? 'comprado' : 'pendente');
    });
    if (!items.length) composer.empty('Nenhum item corresponde às opções escolhidas.');
    return;
  }

  if (id === 'cotacoes') {
    const items = [...(toArr(trip.cotacoes ?? {}) as Cotacao[])].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    composer.section('Cotações', 'Alternativas pesquisadas durante o planejamento.');
    items.forEach(item => {
      const context = [item.cidade, item.origem && item.destino ? `${item.origem} - ${item.destino}` : '', item.data ? dateLabel(item.data) : '', item.site].filter(Boolean).join(' · ');
      composer.card(item.nome, [item.cat, context, options.includeValues && item.preco ? money(item.preco) : ''].filter(Boolean), item.notas, item.status);
    });
    return;
  }

  const items = [...(toArr(trip.docs ?? {}) as TripDoc[])].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
  composer.section('Documentos', 'Índice dos arquivos vinculados à viagem. Links e conteúdo sensível não são reproduzidos.');
  items.forEach(item => composer.card(item.name, [item.cat, item.createdAt ? `Adicionado em ${dateLabel(new Date(item.createdAt).toISOString().slice(0, 10))}` : ''], item.note, 'arquivo'));
}

export async function generateTripPdf(): Promise<void> {
  const ids = selectedAvailableIds();
  if (!ids.length) { toastError('Selecione ao menos uma seção com conteúdo.'); return; }
  const trip = getTrip();
  const button = document.getElementById('export-generate-btn') as HTMLButtonElement | null;
  const original = button?.innerHTML ?? '';
  const previewWindow = window.open('', '_blank');
  if (previewWindow) {
    previewWindow.opener = null;
    previewWindow.document.title = 'Preparando seu guia - Travelle';
    previewWindow.document.body.innerHTML = '<div style="min-height:100vh;display:grid;place-items:center;background:#f4f6f9;color:#0f1923;font:500 15px system-ui"><div style="text-align:center"><div style="font-size:28px;margin-bottom:12px">Travelle</div><div>Preparando seu guia de viagem...</div></div></div>';
  }
  const options: ExportOptions = {
    includeNotes: (document.getElementById('export-notes') as HTMLInputElement | null)?.checked ?? true,
    includeValues: (document.getElementById('export-values') as HTMLInputElement | null)?.checked ?? true,
    includeCompleted: (document.getElementById('export-completed') as HTMLInputElement | null)?.checked ?? true,
    title: (document.getElementById('export-document-title') as HTMLInputElement | null)?.value.trim() || `Guia da viagem · ${trip.name ?? 'Minha viagem'}`,
  };
  try {
    if (button) { button.disabled = true; button.innerHTML = '<span>Montando seu guia…</span><b class="export-spinner"></b>'; }
    const [{ jsPDF }, cover, brandIcon] = await Promise.all([
      import('jspdf'), coverImageData(trip.coverUrl), rasterImageData('/favicon.svg', 256, 256, 'image/png'),
    ]);
    const doc = new jsPDF({ unit: 'mm', format: 'a4', compress: true, putOnlyUsedFonts: true });
    const composer = new PdfComposer(doc, trip, options, brandIcon);
    composer.cover(cover);
    ids.forEach(id => renderPdfSection(composer, id, trip, options));
    composer.finish();
    if (previewWindow && !previewWindow.closed) {
      const blobUrl = URL.createObjectURL(doc.output('blob'));
      previewWindow.location.replace(blobUrl);
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 10 * 60 * 1000);
      toastSuccess('PDF aberto em uma nova aba!');
    } else {
      doc.save(exportFileName(trip.name));
      toastSuccess('O navegador bloqueou a nova aba; o PDF foi baixado.');
    }
  } catch (error) {
    previewWindow?.close();
    console.error('[exportar] Falha ao gerar PDF:', error);
    toastError('Não foi possível gerar o PDF. Tente novamente.');
  } finally {
    if (button) { button.disabled = false; button.innerHTML = original; }
  }
}

export function registerExportGlobals(): void {
  const w = window as unknown as Record<string, unknown>;
  w['__toggleExportSection'] = toggleExportSection;
  w['__setExportPreset'] = setExportPreset;
  w['__generateTripPdf'] = generateTripPdf;
}
